import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, MapPin, Ruler, Gauge } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { IMAGES, REAL_PROJECT_PHOTOS } from "../data/assets";

const easeOut = [0.16, 1, 0.3, 1];

const projectImages = [IMAGES.project_1, IMAGES.project_2, IMAGES.project_3];

export default function Projects() {
  const { t } = useLang();
  const items = t.projects.items;
  const [active, setActive] = useState(0);

  return (
    <section
      id="projects"
      data-testid="projects-section"
      className="relative py-24 sm:py-32 bg-[#0A0A0B] overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#D4AF37]" />
              <span className="overline">{t.projects.eyebrow}</span>
            </div>
            <h2 className="font-display font-bold text-[clamp(2rem,4vw,3.5rem)] text-white tracking-tight leading-[1.05] max-w-[20ch]">
              {t.projects.title}
            </h2>
          </div>
          <p className="text-[#A1A1AA] max-w-md text-base leading-relaxed">
            {t.projects.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Active preview */}
          <motion.div
            key={active}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: easeOut }}
            className="lg:col-span-7 relative aspect-[4/3] lg:aspect-[16/11] bg-[#121214] overflow-hidden group"
          >
            <img
              src={projectImages[active]}
              alt={items[active].name}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B]/95 via-[#0A0A0B]/40 to-transparent" />

            {/* blueprint overlay */}
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 blueprint-bg pointer-events-none" />

            {/* corner markers */}
            <div className="absolute top-5 left-5 right-5 flex items-center justify-between">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">
                <span className="w-1.5 h-1.5 bg-[#D4AF37] animate-pulse" />
                Case {String(active + 1).padStart(2, "0")} / 0{items.length}
              </div>
              <ArrowUpRight className="w-5 h-5 text-[#D4AF37]" />
            </div>

            {/* data chips */}
            <div className="absolute top-1/3 left-8 opacity-0 group-hover:opacity-100 transition-all duration-500 delay-100 hidden lg:block">
              <div className="bg-black/70 border border-[#D4AF37]/40 backdrop-blur-sm px-3 py-2">
                <div className="flex items-center gap-2 text-[10px] text-[#D4AF37] uppercase tracking-widest">
                  <Ruler className="w-3 h-3" />
                  <span>DN 1219 mm</span>
                </div>
              </div>
            </div>
            <div className="absolute bottom-1/3 right-12 opacity-0 group-hover:opacity-100 transition-all duration-500 delay-200 hidden lg:block">
              <div className="bg-black/70 border border-[#D4AF37]/40 backdrop-blur-sm px-3 py-2">
                <div className="flex items-center gap-2 text-[10px] text-[#D4AF37] uppercase tracking-widest">
                  <Gauge className="w-3 h-3" />
                  <span>72 bar · ANSI 600</span>
                </div>
              </div>
            </div>

            <div className="absolute bottom-6 left-6 right-6">
              <div className="text-[11px] text-[#D4AF37] uppercase tracking-[0.3em] mb-2">
                {items[active].meta}
              </div>
              <div className="font-display font-bold text-white text-2xl lg:text-3xl tracking-tight max-w-[22ch]">
                {items[active].name}
              </div>
              <div className="mt-3 text-sm text-[#A1A1AA] max-w-xl leading-relaxed">
                {items[active].desc}
              </div>
            </div>
          </motion.div>

          {/* Project selector */}
          <div className="lg:col-span-5 flex flex-col">
            <div className="flex-1 space-y-3">
              {items.map((p, i) => (
                <button
                  key={i}
                  data-testid={`project-${i}`}
                  onClick={() => setActive(i)}
                  className={`group w-full text-left p-5 lg:p-6 border transition-all duration-400 ${
                    active === i
                      ? "border-[#D4AF37]/60 bg-[#121214]"
                      : "border-white/5 bg-[#0F0F11] hover:border-white/20"
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-2">
                        {String(i + 1).padStart(2, "0")} · {p.meta}
                      </div>
                      <div className="font-display font-bold text-white text-lg lg:text-xl tracking-tight">
                        {p.name}
                      </div>
                    </div>
                    <ArrowUpRight
                      className={`w-5 h-5 flex-shrink-0 mt-1 transition-colors duration-300 ${
                        active === i ? "text-[#D4AF37]" : "text-[#8B939C]"
                      }`}
                    />
                  </div>
                  {active === i && (
                    <div className="mt-4 pt-4 border-t border-white/5 flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]">
                      <MapPin className="w-3 h-3" />
                      {t.projects.tag}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Real project photography gallery */}
        {REAL_PROJECT_PHOTOS.length > 0 && (
          <div className="mt-24 lg:mt-32">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
              <div>
                <div className="flex items-center gap-3 mb-5">
                  <span className="h-px w-10 bg-[#D4AF37]" />
                  <span className="overline">{t.projects.gallery_eyebrow}</span>
                </div>
                <h3 className="font-display font-bold text-white text-[clamp(1.75rem,3vw,2.5rem)] tracking-tight leading-tight max-w-[22ch]">
                  {t.projects.gallery_title}
                </h3>
                <p className="mt-3 text-[#A1A1AA] text-sm max-w-xl leading-relaxed">
                  {t.projects.gallery_subtitle}
                </p>
              </div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-[#8B939C]">
                {String(REAL_PROJECT_PHOTOS.length).padStart(2, "0")} / {String(REAL_PROJECT_PHOTOS.length).padStart(2, "0")} · {t.projects.eyebrow}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 lg:gap-3">
              {REAL_PROJECT_PHOTOS.map((p, i) => {
                // Dynamic bento sizing — featured items every 5
                const isFeatured = i % 5 === 0;
                const span = isFeatured
                  ? "md:col-span-2 md:row-span-2 aspect-square"
                  : "md:col-span-1 aspect-[4/3]";
                return (
                  <motion.figure
                    key={i}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.8, delay: i * 0.04, ease: easeOut }}
                    data-testid={`gallery-photo-${i}`}
                    className={`relative group overflow-hidden bg-[#0F0F11] border border-white/5 hover:border-[#D4AF37]/40 transition-colors duration-500 ${span}`}
                  >
                    <img
                      src={p.url}
                      alt={p.tag}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.04]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-transparent to-transparent opacity-70" />

                    {/* corner brackets */}
                    <div className="absolute top-2 left-2 w-3 h-3 border-t border-l border-[#D4AF37]/70 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="absolute top-2 right-2 w-3 h-3 border-t border-r border-[#D4AF37]/70 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="absolute bottom-2 left-2 w-3 h-3 border-b border-l border-[#D4AF37]/70 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="absolute bottom-2 right-2 w-3 h-3 border-b border-r border-[#D4AF37]/70 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                    <figcaption className="absolute bottom-0 left-0 right-0 p-4 lg:p-5 translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                      <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">
                        {p.tag}
                      </div>
                      <div className="text-white text-sm mt-1 font-display font-semibold">
                        {p.location}
                      </div>
                    </figcaption>
                  </motion.figure>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
